import { useParams, Navigate, Link } from 'react-router-dom';
import { useEffect } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { getPostBySlug } from '../data/posts';
import { trackBlogPostView, trackTagClick } from '../utils/analytics';
import styles from '../styles/BlogPost.module.css';

function BlogPost() {
    const { slug } = useParams<{ slug: string }>();
    const post = slug ? getPostBySlug(slug) : undefined;

    // Track blog post view
    useEffect(() => {
        if (post) {
            trackBlogPostView(post.slug, post.title);
        }
    }, [post]);

    if (!slug) {
        return <Navigate to="/" replace />;
    }

    if (!post) {
        return <Navigate to="/404" replace />;
    }

    const handleTagClick = (tag: string) => {
        trackTagClick(tag, 'blog_post');
    };

    const components: Components = {
        code({ node, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            let codeString = '';
            if (typeof children === 'string') {
                codeString = children;
            } else if (Array.isArray(children)) {
                codeString = children.join('');
            } else if (children !== null && children !== undefined) {
                // eslint-disable-next-line @typescript-eslint/no-base-to-string
                codeString = String(children);
            }
            
            // Check if it's a code block (has language class) or inline code
            const isCodeBlock = match && className?.includes('language-');
            
            return isCodeBlock ? (
                <SyntaxHighlighter
                    style={oneDark as { [key: string]: React.CSSProperties }}
                    language={match[1]}
                    PreTag="div"
                >
                    {codeString.replace(/\n$/, '')}
                </SyntaxHighlighter>
            ) : (
                <code className={className} {...props}>
                    {children}
                </code>
            );
        },
    };

    return (
        <article className={styles.post}>
            <header className={styles.header}>
                <h1>{post.title}</h1>
                <time dateTime={post.date}>
                    {new Date(post.date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                    })}
                </time>
                {post.tags && post.tags.length > 0 && (
                    <div className={styles.tags}>
                        {post.tags.map((tag) => (
                            <Link
                                key={tag}
                                to={`/blog/tag/${encodeURIComponent(tag)}`}
                                className={styles.tag}
                                onClick={() => handleTagClick(tag)}
                            >
                                {tag}
                            </Link>
                        ))}
                    </div>
                )}
            </header>
            <div className={styles.content}>
                <ReactMarkdown components={components}>{post.content}</ReactMarkdown>
            </div>
        </article>
    );
}

export default BlogPost;
